import React from 'react';
import { Bot, PencilRuler, Send, BarChart, Sparkles } from 'lucide-react';
import { LeadFormModal } from '../components/LeadFormModal';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { Breadcrumb } from '../components/Breadcrumb';
import { WhatNextSection } from '../components/WhatNextSection';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { FeatureCard } from '../components/ui/FeatureCard';

export const ContentFlowAiPage: React.FC = () => {
    usePageMetadata({
        title: "ContentFlow AI | Automatyzacja Marketingu w Social Media",
        description: "Zautomatyzuj swój marketing. ContentFlow AI planuje, tworzy i publikuje angażujące posty, grafiki i wideo na social media. Oszczędź czas teraz!",
        schema: {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "ContentFlow AI (Social Media)",
            "serviceType": "Automatyzacja Content Marketingu",
            "provider": {
                "@type": "Organization",
                "name": "BiznesBezKlikania.pl"
            },
            "description": "Kompletny system do automatyzacji marketingu w mediach społecznościowych. AI planuje strategię, tworzy treści (posty, grafiki, wideo) i dystrybuuje je na wielu platformach.",
            "url": "https://biznesbezklikania.pl/rozwiazania/contentflow-ai"
        }
    });

    const features = [
        { icon: <Bot className="w-8 h-8" />, title: 'Strategia AI', description: 'Analiza trendów i konkurencji, aby stworzyć strategię contentową, która naprawdę działa i generuje zasięgi.' },
        { icon: <PencilRuler className="w-8 h-8" />, title: 'Kreacja Multimedialna', description: 'Generowanie artykułów blogowych, postów, grafik, a nawet wideo i podcastów przy użyciu najnowszych modeli AI.' },
        { icon: <Send className="w-8 h-8" />, title: 'Inteligentna Dystrybucja', description: 'Automatyczna publikacja na wszystkich platformach (LinkedIn, Facebook, X, Instagram) w optymalnym czasie.' },
        { icon: <BarChart className="w-8 h-8" />, title: 'Auto-Optymalizacja', description: 'System uczy się, które treści działają najlepiej i automatycznie dostosowuje strategię, aby maksymalizować wyniki.' },
    ];

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[60vh] flex items-center overflow-hidden py-20 md:py-24">
                <ParticlesBackground variant="minimal" />
                <AnimatedBlob variant="mixed" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 text-center relative z-10">
                    <RevealOnScroll>
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 dark:bg-white/5 backdrop-blur-sm border border-white/20 dark:border-white/10 mb-6">
                            <Sparkles className="w-4 h-4 text-primary-light dark:text-primary-dark" />
                            <span className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
                                Automatyzacja Content Marketingu
                            </span>
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                            ContentFlow AI
                        </h1>
                        <p className="text-sm font-semibold text-primary-light dark:text-primary-dark uppercase tracking-wider mb-4">
                            Social Media na Autopilocie
                        </p>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto mb-10">
                            Twoja osobista fabryka treści. Zaplanuj content na cały rok w jeden dzień. AI napisze posty, stworzy grafiki i opublikuje je za Ciebie.
                        </p>
                    </RevealOnScroll>

                    <RevealOnScroll delay={200}>
                        <LeadFormModal
                            productSlug="contentflow-ai"
                            triggerLabel="Zautomatyzuj swój marketing"
                            triggerClassName="px-8 py-4 font-semibold text-white bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark rounded-xl hover:shadow-xl hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30 hover:scale-[1.02] transition-all duration-300 text-lg inline-flex items-center group animate-glow"
                        />
                    </RevealOnScroll>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            <Breadcrumb />

            {/* Features Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="lines" animated={false} />
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Kompletna Automatyzacja Content Marketingu
                        </h2>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mt-4 max-w-2xl mx-auto">
                            Od strategii i kreacji, po dystrybucję i analitykę. Jeden system, który robi wszystko.
                        </p>
                    </RevealOnScroll>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {features.map((feature, index) => (
                            <RevealOnScroll key={feature.title} delay={index * 100}>
                                <FeatureCard
                                    icon={feature.icon}
                                    title={feature.title}
                                    description={feature.description}
                                />
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            <WhatNextSection diyLink="/rozwiazania/contentflow-ai" />
        </>
    );
};
